// src/components/features/brands/forms/RegionalSettingsForm.jsx
import React from 'react';
import {
  Grid,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Autocomplete,
  useTheme,
  alpha,
  FormHelperText,
  Alert,
  Divider,
} from '@mui/material';
import {
  Public as PublicIcon,
  Language as LanguageIcon,
  Translate as TranslateIcon,
  Flag as FlagIcon,
  Place as LocationIcon,
  FormatQuote as QuoteIcon,
} from '@mui/icons-material';
import { FormSection } from '../../../design-system/components/forms';

// Language options
const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English Only' },
  { value: 'ar', label: 'Arabic Only' },
  { value: 'bilingual', label: 'Bilingual (English & Arabic)' },
];

// Dialect options
const DIALECT_OPTIONS = [
  { value: 'standard', label: 'Standard Arabic' },
  { value: 'egyptian', label: 'Egyptian Dialect' },
  { value: 'gulf', label: 'Gulf Dialect' },
  { value: 'levantine', label: 'Levantine Dialect' },
  { value: 'na', label: 'Not Applicable' },
];

// Region options
const REGION_OPTIONS = [
  'Egypt',
  'Saudi Arabia',
  'Qatar',
  'UAE',
  'Kuwait',
  'Bahrain',
  'Oman',
  'Global',
  'Other'
];

const RegionalSettingsForm = ({ 
  brandData, 
  onChange, 
  onNestedChange,
  errors = {},
  touched = {},
  setFieldValue, // For Formik integration
  requiredFields = [], // Fields that are required
}) => {
  const theme = useTheme();
  
  
  // Get regional settings or initialize with defaults
  const regionalSettings = brandData.regionalSettings || {
    primaryLanguage: 'bilingual',
    dialect: 'standard',
    targetRegions: ['Egypt'],
    culturalConsiderations: []
  };

  // Format errors for better display
  const formattedErrors = {
    ...errors,
    regionalSettings: {
      ...(typeof errors.regionalSettings === 'object' ? errors.regionalSettings : {}),
      primaryLanguage: errors['regionalSettings.primaryLanguage'],
      dialect: errors['regionalSettings.dialect'],
      targetRegions: errors['regionalSettings.targetRegions'],
      culturalConsiderations: errors['regionalSettings.culturalConsiderations']
    }
  };

  // Format touched state for proper error display
  const formattedTouched = {
    ...touched,
    regionalSettings: {
      ...(typeof touched.regionalSettings === 'object' ? touched.regionalSettings : {}),
      primaryLanguage: touched['regionalSettings.primaryLanguage'],
      dialect: touched['regionalSettings.dialect'],
      targetRegions: touched['regionalSettings.targetRegions'],
      culturalConsiderations: touched['regionalSettings.culturalConsiderations']
    }
  };

  // Check if a field is required
  const isRequired = (field) => {
    return requiredFields.includes(field) || 
           requiredFields.includes(`regionalSettings.${field}`);
  };
  
  // Handle language selection
  const handleLanguageChange = (e) => {
    const value = e.target.value;
    
    if (setFieldValue) {
      setFieldValue('regionalSettings.primaryLanguage', value);
      // If changing to English only, update dialect to N/A
      if (value === 'en') {
        setFieldValue('regionalSettings.dialect', 'na');
      }
    } else {
      onNestedChange('regionalSettings', 'primaryLanguage', value);
      // If changing to English only, update dialect to N/A
      if (value === 'en') {
        onNestedChange('regionalSettings', 'dialect', 'na');
      }
    }
  };
  
  // Handle dialect selection
  const handleDialectChange = (e) => {
    const value = e.target.value;
    if (setFieldValue) {
      setFieldValue('regionalSettings.dialect', value);
    } else {
      onNestedChange('regionalSettings', 'dialect', value);
    }
  };
  
  // Handle target regions update
  const handleRegionsChange = (e, newValue) => {
    if (setFieldValue) {
      setFieldValue('regionalSettings.targetRegions', newValue);
    } else {
      onNestedChange('regionalSettings', 'targetRegions', newValue);
    }
  };
  
  // Handle cultural considerations update
  const handleConsiderationsChange = (e, newValue) => {
    if (setFieldValue) {
      setFieldValue('regionalSettings.culturalConsiderations', newValue);
    } else {
      onNestedChange('regionalSettings', 'culturalConsiderations', newValue);
    }
  };
  
  // Check for primary language error
  const hasLanguageError = formattedErrors.regionalSettings?.primaryLanguage && 
    formattedTouched.regionalSettings?.primaryLanguage;

  // Check for dialect error
  const hasDialectError = formattedErrors.regionalSettings?.dialect && 
    formattedTouched.regionalSettings?.dialect;

  // Check for target regions error
  const hasRegionsError = formattedErrors.regionalSettings?.targetRegions && 
    formattedTouched.regionalSettings?.targetRegions;

  // Check for considerations error
  const hasConsiderationsError = formattedErrors.regionalSettings?.culturalConsiderations && 
    formattedTouched.regionalSettings?.culturalConsiderations;
  
  return (
    <FormSection
      title="Regional Settings"
      description="Configure language preferences and regional adaptations"
      icon={<LanguageIcon />}
    >
      {/* Show overall section errors if any */}
      {Object.keys(formattedErrors.regionalSettings || {}).length > 0 &&
        Object.values(formattedTouched.regionalSettings || {}).some(t => t) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Please correct the errors in the regional settings to continue.
        </Alert>
      )}
      
      <Grid container spacing={3}>
        {/* Primary Language */}
        <Grid item xs={12} md={6}>
          <FormControl 
            fullWidth
            error={hasLanguageError}
          >
            <InputLabel id="language-label">
              Primary Language{isRequired('primaryLanguage') && ' *'}
            </InputLabel>
            <Select
              labelId="language-label"
              id="primaryLanguage"
              value={regionalSettings.primaryLanguage || ''}
              onChange={handleLanguageChange}
              label={`Primary Language${isRequired('primaryLanguage') ? ' *' : ''}`}
              startAdornment={
                <LanguageIcon color="action" sx={{ ml: 1, mr: 1 }} />
              }
            >
              {LANGUAGE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {hasLanguageError && (
              <FormHelperText error>
                {formattedErrors.regionalSettings.primaryLanguage}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
        
        {/* Dialect Selection (if Arabic is included) */}
        <Grid item xs={12} md={6}>
          <FormControl 
            fullWidth
            disabled={regionalSettings.primaryLanguage === 'en'}
            error={hasDialectError}
          >
            <InputLabel id="dialect-label">
              Arabic Dialect{isRequired('dialect') && ' *'}
            </InputLabel>
            <Select
              labelId="dialect-label"
              id="dialect"
              value={regionalSettings.dialect || ''}
              onChange={handleDialectChange}
              label={`Arabic Dialect${isRequired('dialect') ? ' *' : ''}`}
              startAdornment={
                <TranslateIcon color="action" sx={{ ml: 1, mr: 1 }} />
              }
            >
              {DIALECT_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {hasDialectError && (
              <FormHelperText error>
                {formattedErrors.regionalSettings.dialect}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
        
        {/* Section Divider */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
            Regional Preferences
          </Typography>
        </Grid>
        
        {/* Target Regions */}
        <Grid item xs={12}>
          <Autocomplete
            multiple
            id="target-regions"
            options={REGION_OPTIONS}
            value={regionalSettings.targetRegions || []}
            onChange={handleRegionsChange}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={index}
                  label={option}
                  {...getTagProps({ index })}
                  icon={<LocationIcon />}
                  variant="outlined"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={`Target Regions${isRequired('targetRegions') ? ' *' : ''}`}
                placeholder="Select target regions"
                fullWidth
                error={hasRegionsError}
                helperText={hasRegionsError ? formattedErrors.regionalSettings.targetRegions : ''}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <FlagIcon color="action" sx={{ mr: 1 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        
        {/* Cultural Considerations */}
        <Grid item xs={12}>
          <Autocomplete
            multiple
            id="cultural-considerations"
            options={[]}
            freeSolo
            value={regionalSettings.culturalConsiderations || []}
            onChange={handleConsiderationsChange}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={index}
                  label={option}
                  {...getTagProps({ index })}
                  variant="outlined"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={`Cultural Considerations${isRequired('culturalConsiderations') ? ' *' : ''}`}
                placeholder="Add cultural consideration and press Enter"
                helperText={hasConsiderationsError 
                  ? formattedErrors.regionalSettings.culturalConsiderations 
                  : "e.g., Ramadan sensitivity, Local holidays, Cultural values"}
                fullWidth
                error={hasConsiderationsError}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <QuoteIcon color="action" sx={{ mr: 1 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
      </Grid>
    </FormSection>
  );
};

export default RegionalSettingsForm;